import * as Sentry from "@sentry/react";
import { getCookieConsent } from "@/lib/cookieConsent";

const SENTRY_DSN =
  "https://ca79ad3fc70a4e26801405845819e899@o4511347123027968.ingest.de.sentry.io/4511347149242448";

let initialised = false;
let replayIntegration: ReturnType<typeof Sentry.replayIntegration> | null = null;

export function initSentry() {
  if (initialised) return;
  if (!SENTRY_DSN) return;

  // Replay integration is registered but only started after analytics consent.
  replayIntegration = Sentry.replayIntegration({
    maskAllText: true,
    blockAllMedia: true,
  });

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || "nursly@dev",
    integrations: [Sentry.browserTracingIntegration(), replayIntegration],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.request?.cookies) delete event.request.cookies;
      if (event.user?.ip_address) delete event.user.ip_address;
      return event;
    },
  });

  initialised = true;

  applyConsent();
  window.addEventListener("nursly:consent-changed", applyConsent);
}

function applyConsent() {
  const consent = getCookieConsent();
  const replay = Sentry.getReplay?.();
  if (!replay) return;
  if (consent?.analytics) {
    try { replay.start(); } catch { /* already running */ }
  } else {
    try { replay.stop(); } catch { /* not running */ }
  }
}

export function setSentryUser(user: {
  id?: string;
  role?: string;
  nursery_id?: string;
  plan?: string;
} | null) {
  if (!initialised) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id,
  });
  Sentry.setTags({
    role: user.role || "unknown",
    nursery_id: user.nursery_id || "none",
    plan: user.plan || "none",
  });
}

export { Sentry };
